/**
 * Wrapper for making network requests using es6 native `fetch`
 * @type class
 * @name RestClient
 */

import 'whatwg-fetch';
import 'babel-polyfill';
import Browser from '../utils/browser';

const getToken = () => {
  try {
    return JSON.parse(window.localStorage.getItem('tokenData')).access_token;
  } catch (e) {
    return null;
  }
};

// getting csrfToken from header meta tag
const getCsrfToken = () => {
  const $el = document.getElementsByName('csrf-token')[0];
  return $el ? $el.content : null;
};

export default class RestClient {
  static options = {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      x_csrf_token: getCsrfToken(),
    },
    credentials: 'include', // include cookies with every request
  };

  /**
   * Parses the Object to query string
   * @param {object} obj JS object
   * @return {object} The parsed query string
   */
  static encodeToUrlParams(obj) {
    if (obj instanceof String) {
      return obj;
    }
    const queryString = Object.keys(obj)
      .reduce((a, k) => {
        a.push(`${k}=${encodeURIComponent(obj[k])}`);
        return a;
      }, []).join('&');
    return `${queryString}`;
  }


  /**
   * Parses the JSON returned by a network request
   * @param {object} response A response from a network request
   * @return {object} The parsed JSON from the request
   */
  static async responseJson(response) {
    try {
      return await response.json();
    } catch (e) {
      console.warn(e);
      return null;
    }
  }

  /**
   * Checks if a network request came back fine, and throws an error if not
   * @param {object} response   A response from a network request
   * @return {object|undefined} Returns either the response, or throws an error
   */
  static checkStatus(response) {
    if (response.ok) {
      return response;
    }
    if (response.status === 401 && !response.url.includes('/user/login')) {
      window.localStorage.removeItem('tokenData');
      window.localStorage.removeItem('hubToken');
      window.location = '/app/account';
    }
    const error = new Error(response.statusText);
    error.response = this.responseJson(response);
    throw error;
  }

  /**
   * Send HTTP requests
   * @param {String} url  API endpoint
   * @param {Object} payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static async request(url, options) {
    const allOptions = { ...this.options, ...options, device: Browser.getWebDevice() };
    const combinedOptions = {
      ...allOptions,
      headers: { ...allOptions.headers, Authorization: `Bearer ${getToken()}` },
    };
    const response = await fetch(url, combinedOptions);
    await this.checkStatus(response);
    return this.responseJson(response);
  }

  /**
   * Send HTTP GET request
   * @param {String} url  API endpoint
   * @param {Object} payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static get(url, payload) {
    const options = { method: 'GET' };
    const queryString = {...payload, device: Browser.getWebDevice()}
    let endpoint = url;
    if (queryString) {
      endpoint += `?${this.encodeToUrlParams(queryString)}`;
    }
    return this.request(endpoint, options);
  }

  /**
   * Send HTTP POST request
   * @param {String} url  API endpoint
   * @param payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static post(url, payload) {
    const queryParams = {...payload, device: Browser.getWebDevice()}
    const options = { method: 'POST', body: JSON.stringify(queryParams) };
    return this.request(url, options);
  }

  /**
   * Send HTTP PUT request
   * @param {String} url  API endpoint
   * @param payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static put(url, payload) {
    const queryParams = {...payload, device: Browser.getWebDevice()}
    const options = { method: 'PUT', body: JSON.stringify(queryParams) };
    return this.request(url, options);
  }

  /**
     * Send HTTP DELETE request
     * @param {String} url  API endpoint
     * @param payload
     * @return {Promise} Returns either the response in json, or throws an error
     */
  static delete(url, payload) {
    const queryParams = {...payload, device: Browser.getWebDevice()}
    const options = { method: 'DELETE', body: JSON.stringify(queryParams) };
    return this.request(url, options);
  }


  /**
   * Send HTTP POST request for form data
   * @param {String} url API endpoint
   * @param payload
   * @return {Promise} Returns either the response in json, or throws an error
   */
  static postWithFile(url, payload) {
    const options = {
      method: 'POST',
      body: payload,
      headers: {
        ...this.options.headers,
      },
    };
    delete options.headers['Content-Type'];
    return this.request(url, options);
  }
}

window.RestClient = RestClient;
